import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useP2PNavigation } from "../context/P2PNavigationContext";
import {
  buildSellerDashboardNavContext,
  getVisibleSellerDashboardModules,
  getVisibleSellerDashboardSubTabs,
  isSubTabActive,
  resolveActiveSellerDashboardModule,
} from "../config/sellerDashboardModules";
import { buildSellerTabNotificationsPath } from "../config/sellerDashboardNotifications";
import DashboardModuleTabBadge from "./DashboardModuleTabBadge";
import { useDashboardTabNotificationCounts } from "../hooks/useDashboardTabNotificationCounts";
import sellerDashboardService from "../services/sellerDashboard.service";

export function useSellerDashboardNav() {
  const { ctx: p2pNav, loading: p2pLoading } = useP2PNavigation();
  const [navEpoch, setNavEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setNavEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);

  const navCtx = useMemo(
    () => buildSellerDashboardNavContext(p2pNav, { p2pLoading }),
    [p2pNav, p2pLoading, navEpoch],
  );
  const modules = useMemo(() => getVisibleSellerDashboardModules(navCtx), [navCtx]);
  const moduleKeys = useMemo(() => new Set(modules.map((m) => m.key)), [modules]);

  return { navCtx, modules, moduleKeys, p2pLoading };
}

export default function SellerDashboardModuleNav({ showSubTabs = true, className = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navCtx, modules, p2pLoading } = useSellerDashboardNav();
  const fetchCounts = useCallback(() => sellerDashboardService.getTabNotificationCounts(), []);
  const tabCounts = useDashboardTabNotificationCounts({
    enabled: navCtx.canAccessSellerDashboard,
    fetchCounts,
    pathname: location.pathname,
  });

  const activeModule = useMemo(
    () => resolveActiveSellerDashboardModule(location.pathname, modules),
    [location.pathname, modules],
  );
  const subTabs = useMemo(
    () =>
      showSubTabs && activeModule?.key !== "overview"
        ? getVisibleSellerDashboardSubTabs(activeModule.key, navCtx)
        : [],
    [showSubTabs, activeModule, navCtx],
  );

  if (!navCtx.canAccessSellerDashboard) return null;
  if (modules.length === 0) return null;

  return (
    <div className={`buyer-dashboard__module-nav ${className}`.trim()}>
      <div className="buyer-dashboard__module-tabs">
        {modules.map((tab) => {
          const isOverview = tab.key === "overview";
          const isActiveModule = activeModule?.key === tab.key;
          const onOverviewPage =
            location.pathname === "/dashboard/seller" || location.pathname === "/dashboard/seller/";

          const notificationCount = Number(tabCounts[tab.key]) || 0;
          const hasNotifications = notificationCount > 0;

          let stateClass = "";
          if (isOverview && onOverviewPage) {
            stateClass = " buyer-dashboard__module-tab--active";
          } else if (isActiveModule && !isOverview) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          } else if (isActiveModule && isOverview) {
            stateClass = " buyer-dashboard__module-tab--active";
          }
          if (hasNotifications) {
            stateClass += " buyer-dashboard__module-tab--has-notifications";
          }

          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`buyer-dashboard__module-tab${stateClass}`}
            >
              <span className="buyer-dashboard__module-tab-label">{tab.label}</span>
              <DashboardModuleTabBadge
                count={notificationCount}
                tabKey={tab.key}
                onNavigate={navigate}
                buildPath={buildSellerTabNotificationsPath}
              />
            </Link>
          );
        })}
      </div>

      {subTabs.length > 0 && (
        <div className="buyer-dashboard__sub-tabs">
          {subTabs.map((tab) => {
            const active = isSubTabActive(location.pathname, tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                className={`buyer-dashboard__sub-tab${active ? " buyer-dashboard__sub-tab--active" : ""}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}

      {p2pLoading && modules.some((m) => m.key === "p2p") && (
        <div className="buyer-dashboard__module-nav-hint" aria-hidden="true">
          Loading module access…
        </div>
      )}
    </div>
  );
}
